/** Languages of the game (GDD §9.6): English by default; the options screen chooses later. */
export type Language = 'en' | 'fr';

/**
 * The message catalogue (CLAUDE.md: every player-facing string goes through it). Only what is drawn
 * so far; the screens brick adds the rest. Texts use the glyph set of the font that draws them.
 */
export const MESSAGES = {
  gameOver: { en: 'GAME OVER', fr: 'GAME OVER' },
  /** The header of the Results screen (GDD v0.10 §8, owner decision 2026-10-12). */
  levelClear: { en: 'LEVEL CLEAR', fr: 'FIN DU NIVEAU' },
  /** The tally line labels of the Results screen (GDD v0.10 §8), drawn in the title font. */
  tallyFormations: { en: 'FORMATIONS', fr: 'FORMATIONS' },
  tallyNoBomb: { en: 'NO BOMB', fr: 'SANS BOMBE' },
  tallyNoMiss: { en: 'NO MISS', fr: 'SANS PERTE' },
  tallyBombs: { en: 'BOMBS', fr: 'BOMBES' },
  tallyTotal: { en: 'TOTAL', fr: 'TOTAL' },
  /** The boss announcement (GDD v0.10 §6), drawn in the title font. */
  warning: { en: 'WARNING', fr: 'ATTENTION' },
  /**
   * The pause menu and its quit confirmation (GDD v0.13 §9.1), in the menu font: capitals, French
   * without accents (arcade style). The header is drawn in the title font.
   */
  pause: { en: 'PAUSE', fr: 'PAUSE' },
  resume: { en: 'RESUME', fr: 'REPRENDRE' },
  quit: { en: 'QUIT', fr: 'QUITTER' },
  quitQuestion: { en: 'QUIT THE RUN', fr: 'QUITTER LA PARTIE' },
  no: { en: 'NO', fr: 'NON' },
  yes: { en: 'YES', fr: 'OUI' },
  /** The title menu and the options screens (GDD v0.14 §9.3). */
  play: { en: 'PLAY', fr: 'JOUER' },
  options: { en: 'OPTIONS', fr: 'OPTIONS' },
  visualEffects: { en: 'VISUAL EFFECTS', fr: 'EFFETS VISUELS' },
  /** The *Audio* sub-screen (GDD v0.23 §9.3). */
  audio: { en: 'AUDIO', fr: 'AUDIO' },
  music: { en: 'MUSIC', fr: 'MUSIQUE' },
  soundEffects: { en: 'SOUND EFFECTS', fr: 'BRUITAGES' },
  autofire: { en: 'AUTOFIRE', fr: 'TIR AUTO' },
  language: { en: 'LANGUAGE', fr: 'LANGUE' },
  fullscreen: { en: 'FULLSCREEN', fr: 'PLEIN ECRAN' },
  back: { en: 'BACK', fr: 'RETOUR' },
  allOn: { en: 'ALL ON', fr: 'TOUT ACTIVER' },
  allOff: { en: 'ALL OFF', fr: 'TOUT COUPER' },
  blink: { en: 'BLINK', fr: 'CLIGNOTEMENT' },
  hitFlash: { en: 'HIT FLASH', fr: 'FLASH IMPACT' },
  explosions: { en: 'EXPLOSIONS', fr: 'EXPLOSIONS' },
  shake: { en: 'SHAKE', fr: 'SECOUSSE' },
  bombFlash: { en: 'BOMB FLASH', fr: 'FLASH BOMBE' },
  slowMotion: { en: 'SLOW MOTION', fr: 'RALENTI' },
  championName: { en: 'CHAMPION NAME', fr: 'NOM DU CHAMPION' },
  graze: { en: 'GRAZE', fr: 'FROLEMENT' },
  chainHints: { en: 'CHAIN HINTS', fr: 'AIDE CHAINE' },
  chainStep: { en: 'CHAIN ×2 - KILL FAST', fr: 'CHAINE ×2 - TUE VITE' },
  chainBreak: { en: 'CHAIN BREAK', fr: 'CHAINE ROMPUE' },
  on: { en: 'ON', fr: 'OUI' },
  off: { en: 'OFF', fr: 'NON' },
  /** The controls screen (GDD v0.15 §4.2): header, devices, intents, the capture, restore. */
  controls: { en: 'CONTROLS', fr: 'COMMANDES' },
  keyboard: { en: 'KEYBOARD', fr: 'CLAVIER' },
  gamepad: { en: 'GAMEPAD', fr: 'MANETTE' },
  up: { en: 'UP', fr: 'HAUT' },
  down: { en: 'DOWN', fr: 'BAS' },
  left: { en: 'LEFT', fr: 'GAUCHE' },
  right: { en: 'RIGHT', fr: 'DROITE' },
  fire: { en: 'FIRE', fr: 'TIR' },
  bomb: { en: 'BOMB', fr: 'BOMBE' },
  press: { en: 'PRESS', fr: 'APPUIE' },
  restoreDefaults: { en: 'RESTORE DEFAULTS', fr: 'PAR DEFAUT' },
  /** The keys named by language; letters, digits, arrows and the numpad are not (GDD v0.15). */
  keySpace: { en: 'SPACE', fr: 'ESPACE' },
  keyShiftLeft: { en: 'SHIFT L', fr: 'MAJ G' },
  keyShiftRight: { en: 'SHIFT R', fr: 'MAJ D' },
  keyControlLeft: { en: 'CTRL L', fr: 'CTRL G' },
  keyControlRight: { en: 'CTRL R', fr: 'CTRL D' },
  keyAltLeft: { en: 'ALT L', fr: 'ALT G' },
  keyAltRight: { en: 'ALT R', fr: 'ALT D' },
  keyBackspace: { en: 'BACKSPACE', fr: 'EFFACER' },
  /** The hall of fame (GDD v0.16 §9.1): the High scores heading, the Ending card. */
  highScores: { en: 'HIGH SCORES', fr: 'MEILLEURS SCORES' },
  victory: { en: 'VICTORY', fr: 'VICTOIRE' },
  score: { en: 'SCORE', fr: 'SCORE' },
  /** The name entry (GDD v0.16 §9.1): its two headings and the grid's actions. */
  yourName: { en: 'YOUR NAME', fr: 'TON NOM' },
  country: { en: 'COUNTRY', fr: 'PAYS' },
  delete: { en: 'DEL', fr: 'EFF' },
  ok: { en: 'OK', fr: 'OK' },
  skip: { en: 'SKIP', fr: 'PASSER' },
  noCountry: { en: 'NONE', fr: 'AUCUN' },
  /** The feedback questionnaire (GDD v0.26 §9.1, §9.3): the title row and the offer screen. */
  feedback: { en: 'FEEDBACK', fr: 'AVIS' },
  yourOpinion: { en: 'YOUR OPINION', fr: 'TON AVIS' },
  quickQuestions: { en: '2 QUICK QUESTIONS', fr: '2 QUESTIONS RAPIDES' },
  giveFeedback: { en: 'GIVE FEEDBACK', fr: 'DONNER MON AVIS' },
  later: { en: 'LATER', fr: 'PLUS TARD' },
  openBlocked: { en: 'OPEN BLOCKED', fr: 'OUVERTURE BLOQUEE' },
  /** The gesture prompt: a gamepad cannot open a tab, a tap, click or key can (ADR-0009). */
  gesturePrompt: { en: 'TAP OR PRESS A KEY', fr: 'TOUCHE OU APPUIE' },
  /** A language is always named in itself (GDD v0.14 §9.1). */
  english: { en: 'ENGLISH', fr: 'ENGLISH' },
  francais: { en: 'FRANCAIS', fr: 'FRANCAIS' },
} as const;

/** A key of the catalogue: every player-facing string is looked up by one. */
export type MessageKey = keyof typeof MESSAGES;

/** The catalogue text of `key` in `language`; every player-facing string goes through it. */
export function message(key: MessageKey, language: Language): string {
  return MESSAGES[key][language];
}
